import { useMemo } from 'react';
import { Eye } from 'lucide-react';
import { Badge, IconButton } from '@vnetwork/ui/primitives';
import type { ColumnDef } from '@vnetwork/ui/data';
import type { AuditLog } from '@/api/audit';

// Map action verb → badge tone (failure always overrides to danger in the page).
function actionTone(
  action: string,
): 'danger' | 'success' | 'primary' | 'neutral' {
  if (['delete', 'remove_member', 'revoke_permission'].includes(action)) return 'danger';
  if (['create', 'invite_member', 'grant_permission'].includes(action)) return 'success';
  if (['update', 'update_member', 'update_permission'].includes(action)) return 'primary';
  return 'neutral';
}

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);

/**
 * Column definitions for the audit log DataTable.
 * Extracted to keep ActivityPage under the 200-line budget.
 */
export function useAuditColumns(
  onDetail: (log: AuditLog) => void,
): ColumnDef<AuditLog, unknown>[] {
  return useMemo<ColumnDef<AuditLog, unknown>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        accessorKey: 'created_at',
        enableSorting: false,
        cell: ({ row }) =>
          muted(
            <span style={{ fontFamily: 'var(--vn-font-mono)', whiteSpace: 'nowrap' }}>
              {row.original.created_at}
            </span>,
          ),
      },
      {
        id: 'actor',
        header: 'Actor',
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'] }}>
              {r.actor_email || r.actor_id}
            </span>
          );
        },
      },
      {
        id: 'action',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const tone = r.status === 'failure' ? 'danger' : actionTone(r.action);
          return (
            <Badge tone={tone} appearance="subtle" size="sm">
              {r.action}
            </Badge>
          );
        },
      },
      {
        id: 'resource',
        header: 'Resource',
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span style={{ fontSize: 'var(--vn-font-size-md)' }}>
              <span style={{ color: 'var(--vn-fg-muted)' }}>{r.resource_type} / </span>
              {r.resource_name || r.resource_id || '—'}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original.status;
          return (
            <Badge tone={s === 'success' ? 'success' : 'danger'} appearance="subtle" size="sm">
              {s}
            </Badge>
          );
        },
      },
      {
        id: 'detail',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="View log detail"
              onClick={() => onDetail(row.original)}
            >
              <Eye size={15} />
            </IconButton>
          </div>
        ),
      },
    ],
    [onDetail],
  );
}
